import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Edit2, Trash2, Mail, ArrowLeft, Save, X, Upload, ExternalLink, Image as ImageIcon, Newspaper } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ImageUploader({ onUpload, token }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUpload(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
    } catch (e) { alert('Upload failed'); }
    finally { setUploading(false); fileRef.current.value = ''; }
  };

  return (
    <div className="flex items-center gap-2">
      <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleUpload} className="hidden" />
      <button type="button" onClick={() => fileRef.current.click()} disabled={uploading} className="flex items-center gap-1 px-3 py-1.5 bg-white/10 border border-white/20 rounded text-xs text-white/70 hover:bg-white/15 transition-colors disabled:opacity-50" data-testid="upload-btn">
        <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload'}
      </button>
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pressArticles, setPressArticles] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [tab, setTab] = useState('posts');
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: '', image: '', date: '', read_time: '3 min' });
  const [editingPress, setEditingPress] = useState(null);
  const [showPressForm, setShowPressForm] = useState(false);
  const [pressForm, setPressForm] = useState({ title: '', source: '', url: '', image: '', date: '', excerpt: '' });
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [galleryForm, setGalleryForm] = useState({ image_url: '', label: '', media_type: 'image', order: 0 });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const token = () => localStorage.getItem('admin_token');
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      if (!token()) { setLoading(false); return; }
      const res = await axios.get(`${API}/auth/me`, authHeader());
      setUser(res.data);
      loadData();
    } catch { setLoading(false); }
  };

  const loadData = async () => {
    try {
      const [postsRes, contactsRes, pressRes, galleryRes] = await Promise.all([
        axios.get(`${API}/blog`),
        axios.get(`${API}/admin/contacts`, authHeader()),
        axios.get(`${API}/press`),
        axios.get(`${API}/gallery`),
      ]);
      setPosts(postsRes.data);
      setContacts(contactsRes.data);
      setPressArticles(pressRes.data);
      setGalleryImages(galleryRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      localStorage.setItem('admin_token', res.data.token);
      setUser(res.data);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Login failed');
    }
  };

  const handleLogout = () => { localStorage.removeItem('admin_token'); setUser(null); };

  // Blog CRUD
  const handleSavePost = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) { await axios.put(`${API}/admin/blog/${editingPost.id}`, form, authHeader()); }
      else { await axios.post(`${API}/admin/blog`, form, authHeader()); }
      setShowForm(false); setEditingPost(null);
      setForm({ title: '', excerpt: '', content: '', category: '', image: '', date: '', read_time: '3 min' });
      loadData();
    } catch { alert('Error saving post'); }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await axios.delete(`${API}/admin/blog/${id}`, authHeader()); loadData(); } catch { alert('Error'); }
  };

  const startEditPost = (post) => {
    setEditingPost(post);
    setForm({ title: post.title, excerpt: post.excerpt, content: post.content, category: post.category, image: post.image, date: post.date, read_time: post.read_time });
    setShowForm(true);
  };

  // Press CRUD
  const handleSavePress = async (e) => {
    e.preventDefault();
    try {
      if (editingPress) { await axios.put(`${API}/admin/press/${editingPress.id}`, pressForm, authHeader()); }
      else { await axios.post(`${API}/admin/press`, pressForm, authHeader()); }
      setShowPressForm(false); setEditingPress(null);
      setPressForm({ title: '', source: '', url: '', image: '', date: '', excerpt: '' });
      loadData();
    } catch { alert('Error saving article'); }
  };

  const handleDeletePress = async (id) => {
    if (!window.confirm('Delete this press article?')) return;
    try { await axios.delete(`${API}/admin/press/${id}`, authHeader()); loadData(); } catch { alert('Error'); }
  };

  const startEditPress = (article) => {
    setEditingPress(article);
    setPressForm({ title: article.title, source: article.source, url: article.url, image: article.image, date: article.date, excerpt: article.excerpt || '' });
    setShowPressForm(true);
  };

  // Gallery CRUD
  const handleSaveGallery = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/gallery`, galleryForm, authHeader());
      setShowGalleryForm(false);
      setGalleryForm({ image_url: '', label: '', media_type: 'image', order: 0 });
      loadData();
    } catch { alert('Error saving image'); }
  };

  const handleDeleteGallery = async (id) => {
    if (!window.confirm('Remove this image?')) return;
    try { await axios.delete(`${API}/admin/gallery/${id}`, authHeader()); loadData(); } catch { alert('Error'); }
  };

  // Login screen
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B132B] flex items-center justify-center p-6" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-[#111C3A] rounded-xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Admin Panel</h1>
            <p className="text-[#94A3B8] text-sm">GIT NFT GHANA LTD</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div><Label className="text-white/70 text-sm">Email</Label><Input value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} type="email" required data-testid="admin-login-email" className="bg-white/5 border-white/10 text-white" placeholder="admin@email.com" /></div>
            <div><Label className="text-white/70 text-sm">Password</Label><Input value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} type="password" required data-testid="admin-login-password" className="bg-white/5 border-white/10 text-white" placeholder="********" /></div>
            {loginError && <p className="text-red-400 text-sm" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" data-testid="admin-login-submit" className="w-full py-3 bg-[#00D186] text-[#0B132B] font-bold rounded-sm hover:-translate-y-0.5 transition-transform">Login</button>
          </form>
          <button onClick={() => navigate('/')} className="mt-4 text-[#94A3B8] text-sm hover:text-white transition-colors flex items-center gap-1 mx-auto"><ArrowLeft size={14} /> Back to site</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-[#0B132B] flex items-center justify-center text-white">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#0B132B] text-white" data-testid="admin-panel">
      <div className="border-b border-white/10 bg-[#111C3A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div><h1 className="text-lg font-bold">Admin Dashboard</h1><p className="text-xs text-[#94A3B8]">{user.email}</p></div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-[#94A3B8] hover:text-white text-sm flex items-center gap-1"><ArrowLeft size={14} /> Site</button>
            <button onClick={handleLogout} data-testid="admin-logout" className="text-red-400 hover:text-red-300 flex items-center gap-1 text-sm"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { key: 'posts', label: `Blog (${posts.length})`, icon: null },
            { key: 'press', label: `Press (${pressArticles.length})`, icon: Newspaper },
            { key: 'gallery', label: `Gallery (${galleryImages.length})`, icon: ImageIcon },
            { key: 'contacts', label: `Messages (${contacts.length})`, icon: Mail },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`} className={`px-5 py-2 rounded-sm text-sm font-bold flex items-center gap-1.5 ${tab === t.key ? 'bg-[#00D186] text-[#0B132B]' : 'bg-white/5 text-white/70'}`}>
              {t.icon && <t.icon size={14} />} {t.label}
            </button>
          ))}
        </div>

        {/* Blog Posts Tab */}
        {tab === 'posts' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Blog Posts</h2>
              <button onClick={() => { setShowForm(true); setEditingPost(null); setForm({ title: '', excerpt: '', content: '', category: '', image: '', date: new Date().toISOString().split('T')[0], read_time: '3 min' }); }} data-testid="admin-add-post" className="flex items-center gap-1 px-4 py-2 bg-[#00D186] text-[#0B132B] font-bold rounded-sm text-sm"><Plus size={16} /> New Post</button>
            </div>
            {showForm && (
              <div className="bg-[#111C3A] border border-white/10 rounded-xl p-6 mb-6" data-testid="admin-post-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">{editingPost ? 'Edit Post' : 'New Post'}</h3>
                  <button onClick={() => setShowForm(false)}><X size={18} className="text-white/50" /></button>
                </div>
                <form onSubmit={handleSavePost} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label className="text-white/70 text-sm">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="admin-post-title" className="bg-white/5 border-white/10 text-white" /></div>
                    <div><Label className="text-white/70 text-sm">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="admin-post-category" className="bg-white/5 border-white/10 text-white" placeholder="Digital, Immobilier..." /></div>
                  </div>
                  <div><Label className="text-white/70 text-sm">Excerpt</Label><textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required rows={2} data-testid="admin-post-excerpt" className="w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm resize-none" /></div>
                  <div><Label className="text-white/70 text-sm">Content</Label><textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={4} data-testid="admin-post-content" className="w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm resize-none" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-white/70 text-sm">Image</Label>
                      <div className="flex gap-2">
                        <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required data-testid="admin-post-image" className="bg-white/5 border-white/10 text-white flex-1" placeholder="URL or upload" />
                        <ImageUploader onUpload={(url) => setForm({ ...form, image: url })} token={token()} />
                      </div>
                      {form.image && <img src={form.image} alt="" className="mt-2 h-16 rounded object-cover" />}
                    </div>
                    <div><Label className="text-white/70 text-sm">Date</Label><Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" required data-testid="admin-post-date" className="bg-white/5 border-white/10 text-white" /></div>
                    <div><Label className="text-white/70 text-sm">Read Time</Label><Input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} required className="bg-white/5 border-white/10 text-white" placeholder="3 min" /></div>
                  </div>
                  <button type="submit" data-testid="admin-post-save" className="flex items-center gap-1 px-6 py-2.5 bg-[#00D186] text-[#0B132B] font-bold rounded-sm"><Save size={16} /> Save</button>
                </form>
              </div>
            )}
            <div className="space-y-3">
              {posts.map((post) => (
                <div key={post.id} className="bg-[#111C3A] border border-white/10 rounded-lg p-4 flex items-center gap-4">
                  <img src={post.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0"><h4 className="font-bold text-sm truncate">{post.title}</h4><p className="text-xs text-[#94A3B8]">{post.category} &bull; {post.date}</p></div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEditPost(post)} className="w-8 h-8 rounded bg-white/5 flex items-center justify-center hover:bg-white/10"><Edit2 size={14} /></button>
                    <button onClick={() => handleDeletePost(post.id)} className="w-8 h-8 rounded bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Press Articles Tab */}
        {tab === 'press' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Press Articles</h2>
              <button onClick={() => { setShowPressForm(true); setEditingPress(null); setPressForm({ title: '', source: '', url: '', image: '', date: new Date().toISOString().split('T')[0], excerpt: '' }); }} data-testid="admin-add-press" className="flex items-center gap-1 px-4 py-2 bg-[#00D186] text-[#0B132B] font-bold rounded-sm text-sm"><Plus size={16} /> New Article</button>
            </div>
            {showPressForm && (
              <div className="bg-[#111C3A] border border-white/10 rounded-xl p-6 mb-6" data-testid="admin-press-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">{editingPress ? 'Edit Article' : 'New Press Article'}</h3>
                  <button onClick={() => setShowPressForm(false)}><X size={18} className="text-white/50" /></button>
                </div>
                <form onSubmit={handleSavePress} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label className="text-white/70 text-sm">Title</Label><Input value={pressForm.title} onChange={(e) => setPressForm({ ...pressForm, title: e.target.value })} required data-testid="admin-press-title" className="bg-white/5 border-white/10 text-white" placeholder="Article title" /></div>
                    <div><Label className="text-white/70 text-sm">Source (newspaper/media)</Label><Input value={pressForm.source} onChange={(e) => setPressForm({ ...pressForm, source: e.target.value })} required data-testid="admin-press-source" className="bg-white/5 border-white/10 text-white" placeholder="Le Monde, BBC..." /></div>
                  </div>
                  <div><Label className="text-white/70 text-sm">External URL</Label><Input value={pressForm.url} onChange={(e) => setPressForm({ ...pressForm, url: e.target.value })} type="url" required data-testid="admin-press-url" className="bg-white/5 border-white/10 text-white" placeholder="https://..." /></div>
                  <div><Label className="text-white/70 text-sm">Short description</Label><textarea value={pressForm.excerpt} onChange={(e) => setPressForm({ ...pressForm, excerpt: e.target.value })} rows={2} data-testid="admin-press-excerpt" className="w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm resize-none" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 text-sm">Image</Label>
                      <div className="flex gap-2">
                        <Input value={pressForm.image} onChange={(e) => setPressForm({ ...pressForm, image: e.target.value })} required data-testid="admin-press-image" className="bg-white/5 border-white/10 text-white flex-1" placeholder="URL or upload" />
                        <ImageUploader onUpload={(url) => setPressForm({ ...pressForm, image: url })} token={token()} />
                      </div>
                      {pressForm.image && <img src={pressForm.image} alt="" className="mt-2 h-16 rounded object-cover" />}
                    </div>
                    <div><Label className="text-white/70 text-sm">Date</Label><Input value={pressForm.date} onChange={(e) => setPressForm({ ...pressForm, date: e.target.value })} type="date" required data-testid="admin-press-date" className="bg-white/5 border-white/10 text-white" /></div>
                  </div>
                  <button type="submit" data-testid="admin-press-save" className="flex items-center gap-1 px-6 py-2.5 bg-[#00D186] text-[#0B132B] font-bold rounded-sm"><Save size={16} /> Save</button>
                </form>
              </div>
            )}
            <div className="space-y-3">
              {pressArticles.length === 0 && <p className="text-[#94A3B8] text-sm">No press articles yet. Add your first one!</p>}
              {pressArticles.map((article) => (
                <div key={article.id} className="bg-[#111C3A] border border-white/10 rounded-lg p-4 flex items-center gap-4">
                  {article.image && <img src={article.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm truncate">{article.title}</h4>
                    <p className="text-xs text-[#94A3B8]">{article.source} &bull; {article.date}</p>
                    <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0A75E5] hover:underline flex items-center gap-1 mt-1"><ExternalLink size={10} /> {article.url.slice(0, 50)}...</a>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEditPress(article)} className="w-8 h-8 rounded bg-white/5 flex items-center justify-center hover:bg-white/10"><Edit2 size={14} /></button>
                    <button onClick={() => handleDeletePress(article.id)} className="w-8 h-8 rounded bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {tab === 'gallery' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">GIT ESTATE Gallery</h2>
              <button onClick={() => setShowGalleryForm(true)} data-testid="admin-add-gallery" className="flex items-center gap-1 px-4 py-2 bg-[#00D186] text-[#0B132B] font-bold rounded-sm text-sm"><Plus size={16} /> Add Image/Video</button>
            </div>
            {showGalleryForm && (
              <div className="bg-[#111C3A] border border-white/10 rounded-xl p-6 mb-6" data-testid="admin-gallery-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">Add to Gallery</h3>
                  <button onClick={() => setShowGalleryForm(false)}><X size={18} className="text-white/50" /></button>
                </div>
                <form onSubmit={handleSaveGallery} className="space-y-4">
                  <div>
                    <Label className="text-white/70 text-sm">Image/Video</Label>
                    <div className="flex gap-2">
                      <Input value={galleryForm.image_url} onChange={(e) => setGalleryForm({ ...galleryForm, image_url: e.target.value })} required data-testid="admin-gallery-url" className="bg-white/5 border-white/10 text-white flex-1" placeholder="URL or upload" />
                      <ImageUploader onUpload={(url) => setGalleryForm({ ...galleryForm, image_url: url })} token={token()} />
                    </div>
                    {galleryForm.image_url && galleryForm.media_type === 'image' && <img src={galleryForm.image_url} alt="" className="mt-2 h-20 rounded object-cover" />}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><Label className="text-white/70 text-sm">Label</Label><Input value={galleryForm.label} onChange={(e) => setGalleryForm({ ...galleryForm, label: e.target.value })} required data-testid="admin-gallery-label" className="bg-white/5 border-white/10 text-white" placeholder="Vue aérienne..." /></div>
                    <div>
                      <Label className="text-white/70 text-sm">Type</Label>
                      <select value={galleryForm.media_type} onChange={(e) => setGalleryForm({ ...galleryForm, media_type: e.target.value })} data-testid="admin-gallery-type" className="w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm">
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                      </select>
                    </div>
                    <div><Label className="text-white/70 text-sm">Order</Label><Input value={galleryForm.order} onChange={(e) => setGalleryForm({ ...galleryForm, order: parseInt(e.target.value) || 0 })} type="number" data-testid="admin-gallery-order" className="bg-white/5 border-white/10 text-white" /></div>
                  </div>
                  <button type="submit" data-testid="admin-gallery-save" className="flex items-center gap-1 px-6 py-2.5 bg-[#00D186] text-[#0B132B] font-bold rounded-sm"><Save size={16} /> Add to Gallery</button>
                </form>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.length === 0 && <p className="text-[#94A3B8] text-sm col-span-full">No custom gallery images. Default images are used. Add images here to override.</p>}
              {galleryImages.map((img) => (
                <div key={img.id} className="relative group rounded-xl overflow-hidden border border-white/10">
                  {img.media_type === 'video' ? (
                    <div className="h-40 bg-[#111C3A] flex items-center justify-center"><span className="text-white/50 text-xs">Video: {img.label}</span></div>
                  ) : (
                    <img src={img.image_url} alt={img.label} className="w-full h-40 object-cover" />
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button onClick={() => handleDeleteGallery(img.id)} className="w-10 h-10 rounded-full bg-red-500/80 flex items-center justify-center text-white hover:bg-red-500"><Trash2 size={16} /></button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2">
                    <p className="text-xs text-white truncate">{img.label}</p>
                    <p className="text-[10px] text-white/50">{img.media_type} &bull; #{img.order}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {tab === 'contacts' && (
          <div>
            <h2 className="text-xl font-bold mb-6">Contact Messages</h2>
            <div className="space-y-3">
              {contacts.length === 0 && <p className="text-[#94A3B8]">No messages yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="bg-[#111C3A] border border-white/10 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><span className="font-bold text-sm">{c.name}</span><span className="text-xs text-[#94A3B8]">{c.email}</span></div>
                    <span className="text-xs text-[#94A3B8]">{new Date(c.timestamp).toLocaleDateString()}</span>
                  </div>
                  {c.subject && <p className="text-sm text-[#00D186] mb-1">{c.subject}</p>}
                  <p className="text-sm text-white/70">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
